import { Badge, Button, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import {
  listPartnerMessageThreads,
  listPartnerMessages,
  markPartnerMessagesRead,
  sendPartnerMessage,
} from "../referralPartnersApi";

const PAGE = { page: 0, size: 50 };

const ThreadPicker = ({ threads, selected, onSelect }) => (
  <div className="flex flex-wrap gap-2 mb-3" role="tablist" aria-label="Conversations">
    {threads.map((thread) => {
      const key = thread.clientId ?? "general";
      const active = (selected ?? "general") === key;
      return (
        <Badge key={key} color="error" badgeContent={thread.unread || 0} invisible={!thread.unread}>
          <Chip
            role="tab"
            aria-selected={active}
            color={active ? "primary" : "default"}
            variant={active ? "filled" : "outlined"}
            label={thread.clientId ? thread.clientName || `Client #${thread.clientId}` : "General"}
            onClick={() => onSelect(thread.clientId ?? null)}
          />
        </Badge>
      );
    })}
  </div>
);
const OUTBOUND = "ADMIN_TO_PARTNER";

const formatWhen = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

/**
 * The office half of the partner's conversations. The general thread and each client's thread are
 * kept apart, exactly as the partner sees them: a reply written here goes into the thread that is
 * selected, and notifies the partner where they will look for it.
 */
const PartnerMessagesPanel = ({ partnerId, partnerName, initialClientId = null }) => {
  const queryClient = useQueryClient();
  const canRead = usePermission(P.REFERRAL.MESSAGE_READ);
  const canSend = usePermission(P.REFERRAL.MESSAGE_SEND);
  const [draft, setDraft] = useState("");
  const [clientId, setClientId] = useState(initialClientId);

  const threadsKey = ["referral-partner-message-threads", partnerId];
  const threadsQuery = useQuery({
    queryKey: threadsKey,
    queryFn: () => listPartnerMessageThreads(partnerId),
    enabled: Boolean(partnerId) && canRead,
    refetchOnMount: "always",
  });
  const threads = threadsQuery.data?.content?.length
    ? threadsQuery.data.content
    : [{ clientId: null, unread: 0 }];

  // Newest first, widening window: older messages load on demand instead of capping the
  // thread at the first page.
  const [limit, setLimit] = useState(PAGE.size);
  useEffect(() => setLimit(PAGE.size), [clientId]);
  const queryKey = ["referral-partner-messages", partnerId, clientId, limit];
  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      listPartnerMessages(
        partnerId,
        clientId ? { page: 0, size: limit, clientId } : { page: 0, size: limit }
      ),
    enabled: Boolean(partnerId) && canRead,
    refetchOnMount: "always",
    keepPreviousData: true,
  });

  const messages = useMemo(() => data?.content || [], [data]);
  const hasOlder = (data?.totalItems ?? 0) > messages.length;

  const markRead = useMutation({
    mutationFn: markPartnerMessagesRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: threadsKey });
      queryClient.invalidateQueries(["referral-overview"]);
      queryClient.invalidateQueries(["referral-overview", "partner", partnerId]);
    },
  });

  // Opening the tab is what marks the partner's messages read, the same rule the portal uses.
  const unreadIds = useMemo(
    () =>
      messages
        .filter((message) => message.direction !== OUTBOUND && !message.readAt)
        .map((message) => message.id),
    [messages]
  );

  useEffect(() => {
    if (unreadIds.length && !markRead.isLoading) {
      markRead.mutate(unreadIds);
    }
    // Only the set of unread ids should retrigger this; anything else loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadIds.join(",")]);

  const send = useMutation({
    mutationFn: (body) => sendPartnerMessage(partnerId, { body, clientId }),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: threadsKey });
      queryClient.invalidateQueries(["referral-overview"]);
      queryClient.invalidateQueries(["referral-overview", "partner", partnerId]);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.errorMessage || "Could not send that message."),
  });

  if (!canRead) {
    return (
      <Typography variant="body2" color="text.secondary">
        You do not have access to partner messages.
      </Typography>
    );
  }

  return (
    <div className="border border-app-border rounded-lg p-4">
      <Typography variant="subtitle1" className="font-semibold">
        Messages with {partnerName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The partner sees each conversation where it belongs: general ones in their inbox, client
        ones on that client. Specific issues arrive as support tickets.
      </Typography>

      <ThreadPicker threads={threads} selected={clientId} onSelect={setClientId} />

      {isLoading ? (
        <div className="flex justify-center py-6">
          <CircularProgress size={22} />
        </div>
      ) : isError ? (
        <Typography variant="body2" color="error">
          {error?.response?.data?.errorMessage || "Could not load this thread."}
        </Typography>
      ) : messages.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No messages yet. Anything you write here reaches the partner directly.
        </Typography>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {hasOlder ? (
            <Button
              size="small"
              disabled={isFetching}
              onClick={() => setLimit((current) => current + PAGE.size)}
            >
              {isFetching ? "Loading…" : "Show older messages"}
            </Button>
          ) : null}
          {messages.map((message) => {
            const outbound = message.direction === OUTBOUND;
            return (
              <div
                key={message.id}
                className={`rounded-lg p-3 border ${
                  outbound ? "bg-app-surface border-app-border" : "bg-app-muted border-app-border"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-semibold text-app-text">
                    {outbound ? message.senderName || "Office" : partnerName}
                  </span>
                  <span className="text-xs text-app-text-muted">
                    {formatWhen(message.createdAt)}
                  </span>
                </div>
                {message.clientName ? (
                  <p className="text-xs text-app-text-muted m-0 mt-0.5">
                    About {message.clientName}
                  </p>
                ) : null}
                <p className="text-sm text-app-text whitespace-pre-wrap m-0 mt-1">{message.body}</p>
              </div>
            );
          })}
        </div>
      )}

      {canSend ? (
        <div className="flex items-end gap-2 mt-4">
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Write to the partner"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button
            variant="contained"
            disabled={!draft.trim() || send.isLoading}
            onClick={() => send.mutate(draft.trim())}
          >
            Send
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default PartnerMessagesPanel;
